(function () {
  'use strict';

  /* =========================================================
     All interactions use event delegation on document, so they
     keep working after Shopify's theme editor re-renders a
     section (which destroys directly-bound listeners).
     ========================================================= */

  /* ---------- Shared focus management (WCAG 2.1.2 No Keyboard Trap,
       2.4.3 Focus Order) ----------
     Anything that covers the page keeps Tab inside itself while it is open
     and hands focus back to whatever opened it on close. */
  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
                  'select:not([disabled]),textarea:not([disabled]),iframe,summary,[tabindex]:not([tabindex="-1"])';

  function focusablesIn(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[hidden]')) return false;
      var r = el.getBoundingClientRect();
      if (!r.width && !r.height) return false;
      var cs = getComputedStyle(el);
      return cs.visibility !== 'hidden' && cs.display !== 'none';
    });
  }

  /* Installs a Tab loop on `root`. Returns a function that removes it. */
  function trapFocus(root) {
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = focusablesIn(root);
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0], last = items[items.length - 1];
      var active = document.activeElement;
      if (!root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey, true);
    return function () { document.removeEventListener('keydown', onKey, true); };
  }

  /* Announces a message to screen readers without moving focus. */
  var liveRegion = null;
  function announce(msg) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = msg; }, 60);
  }

  /* Exposed so a section's own inline script (the gallery lightbox) can
     reuse the same trap instead of shipping a second implementation. */
  window.harrisA11y = { trapFocus: trapFocus, focusablesIn: focusablesIn, announce: announce };

  /* ---------- Mobile nav (burger) ----------
     The drawer's show/hide is pure CSS (opacity + transform + visibility) so
     it can animate. JS only flips classes, measures the header offset, and
     locks the page scroll WITHOUT shifting the layout sideways. */
  function measureHeader(header) {
    if (!header) header = document.querySelector('.harris-header');
    if (!header) return;
    var rect = header.getBoundingClientRect();
    document.documentElement.style.setProperty('--harris-header-h', Math.max(0, rect.bottom) + 'px');
  }

  /* Scroll lock that never moves the page. `scrollbar-gutter:stable` keeps the
     gutter reserved in modern browsers, so clientWidth does not change and the
     measured width below is 0. Older engines fall back to compensation, which
     pads the page and the fixed header by the same amount. */
  function lockPage(on) {
    var de = document.documentElement;
    if (on) {
      var sbw = window.innerWidth - de.clientWidth;
      de.style.setProperty('--harris-sbw', (sbw > 0 ? sbw : 0) + 'px');
      de.classList.add('harris-drawer-open');
      document.body.classList.add('harris-nav-open');
    } else {
      de.classList.remove('harris-drawer-open');
      document.body.classList.remove('harris-nav-open');
      de.style.removeProperty('--harris-sbw');
    }
  }

  var releaseDrawerTrap = null;
  function setDrawer(nav, burger, open) {
    nav.classList.toggle('is-open', open);
    if (burger) burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    lockPage(open);

    /* Keyboard users must not be able to Tab out of the open drawer into the
       page behind it, and must land back on the burger when it closes. */
    if (releaseDrawerTrap) { releaseDrawerTrap(); releaseDrawerTrap = null; }
    if (open) {
      releaseDrawerTrap = trapFocus(nav.closest('.harris-header') || nav);
      var first = focusablesIn(nav)[0];
      if (first) setTimeout(function () { first.focus(); }, 60);
    } else if (burger && document.activeElement && nav.contains(document.activeElement)) {
      burger.focus();
    }

    if (!open) {
      /* collapse any expanded rows so the next open starts clean */
      nav.querySelectorAll('.is-expanded').forEach(function (el) {
        el.classList.remove('is-expanded');
        var sub = el.querySelector(':scope > ul, :scope > .harris-mega, :scope > .harris-mega2__list');
        if (sub) sub.style.maxHeight = '';
        var t = el.querySelector(':scope > .harris-subtoggle, :scope > .harris-subtoggle2');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }
  }

  document.addEventListener('click', function (e) {
    var burger = e.target.closest('.harris-burger');
    if (!burger) return;
    var header = burger.closest('.harris-header') || document.querySelector('.harris-header');
    var nav = header && header.querySelector('.harris-nav');
    if (!nav) return;
    measureHeader(header);
    setDrawer(nav, burger, !nav.classList.contains('is-open'));
  });

  /* Esc closes, a link tap closes, and leaving mobile width closes */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var nav = document.querySelector('.harris-nav.is-open');
    if (nav) setDrawer(nav, document.querySelector('.harris-burger'), false);
  });
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.harris-nav.is-open a[href]');
    if (!link || link.closest('.harris-subtoggle, .harris-subtoggle2')) return;
    var nav = link.closest('.harris-nav');
    setDrawer(nav, document.querySelector('.harris-burger'), false);
  });
  window.addEventListener('resize', function () {
    var nav = document.querySelector('.harris-nav.is-open');
    if (nav && window.innerWidth > 960) {
      setDrawer(nav, document.querySelector('.harris-burger'), false);
    } else if (nav) {
      measureHeader(nav.closest('.harris-header'));
    }
  });
  if (document.readyState !== 'loading') measureHeader();
  else document.addEventListener('DOMContentLoaded', function () { measureHeader(); });

  /* ---------- Mobile mega column sub-accordion (+/-) ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('.harris-subtoggle2');
    if (!t) return;
    e.preventDefault();
    var col = t.closest('.harris-mega2__col');
    if (!col) return;
    var open = col.classList.toggle('is-expanded');
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
    slidePanel(col.querySelector('.harris-mega2__list'), open);
    /* growing a column must not be clipped by its open parent panel */
    var parentPanel = col.closest('.harris-mega');
    if (parentPanel && parentPanel.style.maxHeight && parentPanel.style.maxHeight !== 'none' && parentPanel.style.maxHeight !== '0px') {
      parentPanel.style.maxHeight = 'none';
    }
  });

  /* ---------- Mobile accordion: animate to measured height ---------- */
  function slidePanel(el, open) {
    if (!el) return;
    /* Drop any listener left over from an interrupted run, otherwise a fast
       double-tap can unlock the height mid-collapse and cause a pop. */
    if (el._harrisDone) {
      el.removeEventListener('transitionend', el._harrisDone);
      el._harrisDone = null;
    }
    /* Always start from a concrete pixel height so both directions animate. */
    var current = el.getBoundingClientRect().height;
    el.style.maxHeight = current + 'px';
    void el.offsetHeight; /* commit the starting frame */

    if (open) {
      el.style.maxHeight = el.scrollHeight + 'px';
      el._harrisDone = function (ev) {
        if (ev.target !== el || ev.propertyName !== 'max-height') return;
        el.removeEventListener('transitionend', el._harrisDone);
        el._harrisDone = null;
        /* unlock so nested rows can grow it later */
        if (el.parentNode && el.parentNode.classList.contains('is-expanded')) {
          el.style.maxHeight = 'none';
        }
      };
      el.addEventListener('transitionend', el._harrisDone);
    } else {
      requestAnimationFrame(function () { el.style.maxHeight = '0px'; });
    }
  }
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('.harris-subtoggle');
    if (!toggle) return;
    e.preventDefault();
    var li = toggle.closest('li');
    var open = li.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    slidePanel(li.querySelector(':scope > ul, :scope > .harris-mega'), open);
  });

  /* ---------- PDP gallery thumbs ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.harris-pdp__thumb');
    if (!btn) return;
    var mainImg = document.getElementById('harris-main-img');
    if (mainImg) mainImg.src = btn.getAttribute('data-full');
    document.querySelectorAll('.harris-pdp__thumb').forEach(function (b) { b.classList.remove('is-active'); });
    btn.classList.add('is-active');
  });

  /* ---------- PDP variant price sync ---------- */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('#harris-variant-select');
    if (!sel) return;
    var price = sel.options[sel.selectedIndex].getAttribute('data-price');
    var p1 = document.getElementById('harris-price');
    var p2 = document.getElementById('harris-btn-price');
    if (price && p1) p1.textContent = price;
    if (price && p2) p2.textContent = price;
  });

  /* ---------- Money formatting ---------- */
  function moneyFormatter() {
    var cartEl = document.querySelector('.harris-cart');
    var iso = (cartEl && cartEl.getAttribute('data-currency')) || 'USD';
    try {
      var fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: iso });
      return function (cents) { return fmt.format(cents / 100); };
    } catch (err) {
      return function (cents) { return '$' + (cents / 100).toFixed(2); };
    }
  }

  /* ---------- AJAX cart ---------- */
  function showQtyNote(row, msg) {
    var note = row.querySelector('[data-qty-note]');
    if (!note) return;
    note.textContent = msg;
    note.classList.add('is-visible');
    clearTimeout(note._t);
    note._t = setTimeout(function () { note.classList.remove('is-visible'); }, 4000);
  }

  function flashTotal() {
    var tot = document.getElementById('harris-total');
    if (!tot) return;
    tot.classList.add('is-flash');
    setTimeout(function () { tot.classList.remove('is-flash'); }, 400);
  }

  function updateCartDom(cart, ctx) {
    var money = moneyFormatter();
    document.querySelectorAll('.harris-cart-count').forEach(function (el) { el.textContent = cart.item_count; });
    if (cart.item_count === 0) { window.location.reload(); return; }
    var keys = {};
    var lineByKey = {};
    cart.items.forEach(function (item, i) { keys[item.key] = item; lineByKey[item.key] = i + 1; });
    document.querySelectorAll('[data-cart-row]').forEach(function (row) {
      var rowKey = row.getAttribute('data-key');
      var item = keys[rowKey];
      if (!item) { row.remove(); return; }
      row.setAttribute('data-line', lineByKey[rowKey]);
      var qtyInput = row.querySelector('input[name="updates[]"]');
      if (qtyInput) { qtyInput.value = item.quantity; qtyInput.dataset.prev = item.quantity; }
      if (ctx && ctx.key === rowKey && typeof ctx.requested === 'number' && item.quantity < ctx.requested) {
        showQtyNote(row, 'Only ' + item.quantity + ' available — this piece is limited stock.');
      }
      var totalCell = row.querySelector('[data-line-total]');
      if (totalCell) totalCell.textContent = money(item.final_line_price);
      row.classList.remove('is-updating');
    });
    var sub = document.getElementById('harris-subtotal');
    var tot = document.getElementById('harris-total');
    if (sub) sub.textContent = money(cart.items_subtotal_price);
    if (tot) tot.textContent = money(cart.total_price);
    flashTotal();
    announce('Cart updated. Total ' + money(cart.total_price) + '.');
    var discRow = document.getElementById('harris-discount-row');
    if (discRow) {
      if (cart.total_discount > 0) {
        discRow.style.display = '';
        var d = discRow.querySelector('[data-discount]');
        if (d) d.textContent = '\u2212' + money(cart.total_discount);
      } else {
        discRow.style.display = 'none';
      }
    }
  }

  function postChange(params) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: params
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) {
          var err = new Error('Cart update failed');
          err.status = r.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function rejectQtyChange(row, err) {
    if (!row) return;
    row.classList.remove('is-updating');
    var input = row.querySelector('input[name="updates[]"]');
    if (input && input.dataset.prev) input.value = input.dataset.prev;
    var msg = (err && err.data && (err.data.description || err.data.message)) || 'Unable to update quantity.';
    showQtyNote(row, msg);
  }

  function changeLine(key, quantity, row) {
    if (row) row.classList.add('is-updating');
    var ctx = { key: key, requested: quantity };
    var byKey = new URLSearchParams();
    byKey.append('id', key);
    byKey.append('quantity', quantity);
    postChange(byKey)
      .then(function (cart) { updateCartDom(cart, ctx); })
      .catch(function (err) {
        if (err && err.status === 422) { rejectQtyChange(row, err); return; }
        // Fallback: address the row by its line number
        var line = row ? row.getAttribute('data-line') : null;
        if (!line) { window.location.reload(); return; }
        var byLine = new URLSearchParams();
        byLine.append('line', line);
        byLine.append('quantity', quantity);
        postChange(byLine).then(function (cart) {
          updateCartDom(cart, ctx);
        }).catch(function (err2) {
          if (err2 && err2.status === 422) { rejectQtyChange(row, err2); return; }
          window.location.reload();
        });
      });
  }

  var changeTimer = null;
  function scheduleChange(key, quantity, row) {
    clearTimeout(changeTimer);
    changeTimer = setTimeout(function () { changeLine(key, quantity, row); }, 300);
  }

  // Typed quantity changes in the cart
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.matches('input[name="updates[]"]')) return;
    var row = input.closest('[data-cart-row]');
    if (!row) return;
    scheduleChange(row.getAttribute('data-key'), Math.max(0, parseInt(input.value, 10) || 0), row);
  });

  // Remove links
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-cart-remove]');
    if (!link) return;
    e.preventDefault();
    var row = link.closest('[data-cart-row]');
    changeLine(row.getAttribute('data-key'), 0, row);
  });

  /* ---------- Quantity steppers (PDP + cart) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.harris-qty button[data-qty]');
    if (!btn) return;
    var input = btn.closest('.harris-qty').querySelector('input');
    var min = parseInt(input.min || '0', 10);
    input.value = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(btn.getAttribute('data-qty'), 10));
    var row = input.closest('[data-cart-row]');
    if (row) scheduleChange(row.getAttribute('data-key'), parseInt(input.value, 10), row);
  });

  /* ---------- Book an appointment: open the widget in place ----------
     The booking links point at the absolute home-page URL so they still work
     with JavaScript off. With JavaScript on that meant a full navigation to
     the home page before the modal appeared. The Jeweleros script is loaded
     on every page, so the modal can be opened where the visitor already is.
     If it does not appear, we fall through to the original link rather than
     leaving the visitor with a dead button. */
  (function () {
    var FRAG = 'jos-book-appointment';

    /* A modal is a large, newly added fixed-position element. Snapshotting the
       ones that already exist avoids mistaking a chat bubble or a sticky bar
       that was there all along for the booking widget. */
    function fixedPanels() {
      var found = [];
      var all = document.body ? document.body.querySelectorAll('div,iframe,aside,section') : [];
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        var r = el.getBoundingClientRect();
        if (r.width < 220 || r.height < 220) continue;
        var pos = getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'absolute') found.push(el);
      }
      return found;
    }

    function openWidget() {
      /* 1. a public method, if the widget exposes one */
      var api = window.JOS || window.jos || window.Jeweleros || window.jeweleros;
      if (api) {
        var fn = api.open || api.openBooking || api.show || api.toggle;
        if (typeof fn === 'function') {
          try { fn.call(api); return; } catch (err) { /* fall through */ }
        }
      }
      /* 2. an in-page target the widget has already wired up */
      var target = document.getElementById(FRAG) || document.querySelector('[data-jos-book]');
      if (target && typeof target.click === 'function' && target.tagName !== 'A') {
        try { target.click(); } catch (err) { /* fall through */ }
      }
      /* 3. the hash it listens for, set without reloading the page */
      if (window.location.hash !== '#' + FRAG) {
        var here = window.location.pathname + window.location.search + '#' + FRAG;
        if (window.history && window.history.pushState) window.history.pushState(null, '', here);
        else window.location.hash = FRAG;
      }
      var ev;
      try { ev = new HashChangeEvent('hashchange'); }
      catch (err) {
        ev = document.createEvent('Event');
        ev.initEvent('hashchange', true, false);
      }
      window.dispatchEvent(ev);
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('a[href*="' + FRAG + '"]') : null;
      if (!link) return;
      if (link.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;

      var href = link.getAttribute('href') || '';
      var before = fixedPanels();
      e.preventDefault();
      openWidget();

      window.setTimeout(function () {
        var after = fixedPanels();
        for (var i = 0; i < after.length; i++) {
          if (before.indexOf(after[i]) === -1) return; /* something opened */
        }
        window.location.href = href; /* nothing opened — use the link */
      }, 1500);
    });
  })();

  /* ---------- Load More (collections) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    var nextUrl = btn.getAttribute('data-next-url');
    if (!nextUrl) return;
    btn.disabled = true;
    btn.textContent = 'Loading\u2026';
    fetch(nextUrl)
      .then(function (r) { if (!r.ok) throw new Error('Load failed'); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-collection-grid]');
        var grid = document.querySelector('[data-collection-grid]');
        if (newGrid && grid) {
          var added = [];
          Array.prototype.slice.call(newGrid.children).forEach(function (child) {
            grid.appendChild(child);
            added.push(child);
          });
          initReveal(added);
          announce(added.length + ' more products loaded.');
          /* send the keyboard to the first new card rather than leaving focus
             on a button that may have just been removed */
          var firstLink = added.length && added[0].querySelector('a[href]');
          if (firstLink) { firstLink.setAttribute('tabindex', '-1'); firstLink.focus(); }
        }
        var newBtn = doc.querySelector('[data-load-more]');
        if (newBtn && newBtn.getAttribute('data-next-url')) {
          btn.setAttribute('data-next-url', newBtn.getAttribute('data-next-url'));
          btn.disabled = false;
          btn.textContent = 'Load More';
        } else {
          btn.remove();
        }
      })
      .catch(function () { window.location.href = nextUrl; });
  });

  /* ---------- Benefits panel ---------- */
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-benefits-open]');
    var closeEl = e.target.closest('[data-benefits-close]');
    if (!openBtn && !closeEl) return;
    var wrap = (openBtn || closeEl).closest('[data-benefits]');
    if (!wrap) return;
    var open = !!openBtn;
    wrap.classList.toggle('is-open', open);
    var tab = wrap.querySelector('[data-benefits-open]');
    if (tab) tab.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var closeBtn = wrap.querySelector('.harris-benefits__close');
      if (closeBtn) closeBtn.focus();
    } else if (tab) {
      tab.focus();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var wrap = document.querySelector('[data-benefits].is-open');
    if (!wrap) return;
    wrap.classList.remove('is-open');
    var tab = wrap.querySelector('[data-benefits-open]');
    if (tab) { tab.setAttribute('aria-expanded', 'false'); tab.focus(); }
  });
  // Close the panel when a link inside it is followed (e.g. the booking anchor)
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.harris-benefits__panel a');
    if (!link) return;
    var wrap = link.closest('[data-benefits]');
    if (wrap) {
      wrap.classList.remove('is-open');
      var tab = wrap.querySelector('[data-benefits-open]');
      if (tab) tab.setAttribute('aria-expanded', 'false');
    }
  });

  /* ---------- Scroll-reveal ---------- */
  var io = null;
  var revealIndex = 0;
  function ensureObserver() {
    if (io || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    return io;
  }
  function initReveal(elements) {
    var obs = ensureObserver();
    if (!obs) return;
    elements.forEach(function (el) {
      if (el.classList.contains('harris-reveal')) return;
      el.classList.add('harris-reveal');
      el.style.transitionDelay = ((revealIndex % 4) * 70) + 'ms';
      revealIndex++;
      obs.observe(el);
    });
  }
  function scanReveal() {
    initReveal(Array.prototype.slice.call(document.querySelectorAll(
      '.harris-card,.harris-coll-card,.harris-member,.harris-post-card,.harris-stat,.harris-tile,' +
      '.harris-split__media,.harris-split__grid>div:not(.harris-split__media),' +
      '.harris-brand>.harris-wrap,.harris-cta__inner,.harris-regrets__card,.harris-team__head'
    )));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanReveal);
  } else {
    scanReveal();
  }
  // Re-scan when the Shopify theme editor re-renders a section
  document.addEventListener('shopify:section:load', scanReveal);

  /* ---------- Scroll cover/reveal: apply + auto-pair ---------- */
  function positionPinned() {
    // Sections taller than the viewport pin by their BOTTOM edge:
    // they scroll normally until fully read, then freeze for the cover.
    document.querySelectorAll('main .shopify-section.harris-pinned').forEach(function (sec) {
      var offset = Math.min(0, window.innerHeight - sec.offsetHeight);
      sec.style.top = offset + 'px';
    });
  }

  var pinnedRO = ('ResizeObserver' in window) ? new ResizeObserver(positionPinned) : null;

  /* A pinned section may only be visible while its covering partner (the next
     section) still overlaps the viewport. Once the partner has fully scrolled
     past the top, the pinned section is hidden outright — so even if stacking
     ever goes wrong, a stuck section can never overlay the rest of the page.
     Scrolling back up, the partner re-enters and the pinned section reappears
     before the reveal begins, so the effect is seamless in both directions. */
  function capPinnedVisibility() {
    document.querySelectorAll('main .shopify-section.harris-pinned').forEach(function (sec) {
      var next = sec.nextElementSibling;
      while (next && !next.classList.contains('shopify-section')) next = next.nextElementSibling;
      if (!next) { sec.style.visibility = ''; return; }
      var coverBottom = next.getBoundingClientRect().bottom;
      sec.style.visibility = coverBottom <= 0 ? 'hidden' : '';
    });
  }
  var capTick = false;
  function onScrollCap() {
    if (capTick) return;
    capTick = true;
    requestAnimationFrame(function () { capTick = false; capPinnedVisibility(); });
  }
  window.addEventListener('scroll', onScrollCap, { passive: true });

  function applyScrollEffects() {
    var sections = document.querySelectorAll('main .shopify-section');
    sections.forEach(function (sec) {
      sec.classList.remove('harris-pinned', 'harris-over');
      sec.style.top = '';
      sec.style.zIndex = '';
      sec.style.visibility = '';
      if (pinnedRO) pinnedRO.unobserve(sec);
    });
    sections.forEach(function (sec) {
      var eff = getComputedStyle(sec).getPropertyValue('--harris-scroll-effect').trim();
      if (eff === 'pin') sec.classList.add('harris-pinned');
      if (eff === 'over') {
        sec.classList.add('harris-over');
        // Auto-pin the previous section so "slides over" always has something to cover
        var prev = sec.previousElementSibling;
        if (prev && prev.classList.contains('shopify-section')) {
          var prevEff = getComputedStyle(prev).getPropertyValue('--harris-scroll-effect').trim();
          if (prevEff !== 'over') prev.classList.add('harris-pinned');
        }
      }
    });
    // Deterministic stacking: a later section must ALWAYS paint above an
    // earlier one (that's what makes cover/reveal symmetric when scrolling
    // back up). Pinned sections live in a low band so everything after them
    // covers them while they're stuck; all other sections get an ascending
    // z-index in DOM order. Never rely on tie-breaking between equal z values.
    var i = 0;
    sections.forEach(function (sec) {
      i++;
      // Pinned band 1..n sits under the normal band 40+; both stay below
      // page chrome (header 90, benefits tab 88, panels 118+).
      sec.style.zIndex = sec.classList.contains('harris-pinned') ? i : (40 + i);
    });
    positionPinned();
    capPinnedVisibility();
    if (pinnedRO) {
      document.querySelectorAll('main .shopify-section.harris-pinned').forEach(function (sec) {
        pinnedRO.observe(sec);
      });
    }
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      positionPinned();
      if (window.innerWidth > 960) {
        document.querySelectorAll('.harris-nav ul[style], .harris-nav .harris-mega[style], .harris-mega2__list[style]').forEach(function (el) {
          el.style.maxHeight = '';
        });
      }
    }, 150);
  });
  // Re-apply fully once every resource has loaded (final section heights),
  // and again when the page is restored from the back/forward cache.
  window.addEventListener('load', applyScrollEffects);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) applyScrollEffects();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyScrollEffects);
  } else {
    applyScrollEffects();
  }
  document.addEventListener('shopify:section:load', applyScrollEffects);
  document.addEventListener('shopify:section:reorder', applyScrollEffects);


  /* ============================================================
     Harris additions
     ============================================================ */

  /* ---------- Testimonials carousel ---------- */
  document.querySelectorAll('[data-reviews]').forEach(function (root) {
    var track = root.querySelector('[data-reviews-track]');
    if (!track) return;
    function step(dir) {
      var card = track.querySelector('.harris-review');
      var w = card ? card.getBoundingClientRect().width + 24 : track.clientWidth;
      var max = track.scrollWidth - track.clientWidth - 2;
      var next = track.scrollLeft + dir * w;
      if (dir > 0 && track.scrollLeft >= max) next = 0;
      if (dir < 0 && track.scrollLeft <= 2) next = max;
      track.scrollTo({ left: next, behavior: 'smooth' });
    }
    var prev = root.querySelector('[data-reviews-prev]');
    var nextBtn = root.querySelector('[data-reviews-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
  });

  /* ---------- Store locator ---------- */
  document.querySelectorAll('[data-stores]').forEach(function (root) {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-store]');
      if (!btn) return;
      root.querySelectorAll('[data-store]').forEach(function (b) { b.classList.toggle('is-active', b === btn); });
      root.querySelectorAll('[data-store-map]').forEach(function (m) { m.hidden = m.getAttribute('data-store-map') !== btn.getAttribute('data-store'); });
    });
  });

  /* ---------- Local product lists: wishlist + recently viewed ---------- */
  function readList(key) {
    try { var v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; } catch (err) { return []; }
  }
  function writeList(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (err) {}
  }
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function productCard(p, root) {
    var img = p.featured_image ? '<img src="' + escapeHtml(p.featured_image) + '" alt="' + escapeHtml(p.title) + '" width="600" height="600" loading="lazy">' : '';
    var price = p.available ? moneyFormatter()(p.price) : 'Sold out';
    return '<a class="harris-card" href="' + root + 'products/' + encodeURIComponent(p.handle) + '"><div class="harris-card__img">' + img +
      '</div><div class="harris-card__info"><div class="harris-card__name">' + escapeHtml(p.title) + '</div><div class="harris-card__price">' + price + '</div></div></a>';
  }
  function fetchProducts(handles, root) {
    return Promise.all(handles.map(function (h) {
      return fetch(root + 'products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    }));
  }

  var WISH_KEY = 'harris:wishlist';
  function syncWishButtons() {
    var list = readList(WISH_KEY);
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var on = list.indexOf(btn.getAttribute('data-wishlist-toggle')) > -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = label.getAttribute(on ? 'data-remove' : 'data-add');
    });
  }
  function renderWishlist() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var root = page.getAttribute('data-root') || '/';
    if (root.slice(-1) !== '/') root += '/';
    var list = readList(WISH_KEY);
    empty.hidden = list.length > 0;
    if (!list.length) { grid.innerHTML = ''; return; }
    fetchProducts(list, root).then(function (products) {
      grid.innerHTML = products.map(function (p) {
        if (!p) return '';
        return '<div class="harris-wishlist__cell"><button type="button" class="harris-wishlist__remove" data-wishlist-remove="' + escapeHtml(p.handle) + '" aria-label="Remove from wishlist">&times;</button>' + productCard(p, root) + '</div>';
      }).join('');
    });
  }
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-wishlist-toggle]');
    var remove = e.target.closest('[data-wishlist-remove]');
    if (!toggle && !remove) return;
    var handle = (toggle || remove).getAttribute(toggle ? 'data-wishlist-toggle' : 'data-wishlist-remove');
    var list = readList(WISH_KEY);
    var i = list.indexOf(handle);
    if (i > -1) list.splice(i, 1); else if (toggle) list.unshift(handle);
    writeList(WISH_KEY, list);
    syncWishButtons();
    renderWishlist();
  });
  syncWishButtons();
  renderWishlist();

  var RECENT_KEY = 'harris:recent';
  document.querySelectorAll('[data-recently-viewed]').forEach(function (sec) {
    var current = sec.getAttribute('data-current');
    var limit = parseInt(sec.getAttribute('data-limit'), 10) || 4;
    var root = sec.getAttribute('data-root') || '/';
    if (root.slice(-1) !== '/') root += '/';
    var list = readList(RECENT_KEY);
    var others = list.filter(function (h) { return h !== current; }).slice(0, limit);
    if (current) {
      writeList(RECENT_KEY, [current].concat(list.filter(function (h) { return h !== current; })).slice(0, 12));
    }
    if (!others.length) return;
    fetchProducts(others, root).then(function (products) {
      var html = products.map(function (p) { return p ? productCard(p, root) : ''; }).join('');
      if (!html) return;
      sec.querySelector('[data-recent-grid]').innerHTML = html;
      sec.hidden = false;
    });
  });

  /* ---------- Product recommendations ---------- */
  document.querySelectorAll('[data-recommendations]').forEach(function (sec) {
    if (sec.hidden || sec.children.length) return;
    fetch(sec.getAttribute('data-url')).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) sec.innerHTML = fresh.innerHTML;
    }).catch(function () {});
  });


  /* ---------- Collection filters: apply on change ---------- */
  document.querySelectorAll('[data-filter-form]').forEach(function (form) {
    var t;
    form.addEventListener('change', function (e) {
      clearTimeout(t);
      var delay = e.target.type === 'number' ? 700 : 0;
      t = setTimeout(function () { form.submit(); }, delay);
    });
    form.addEventListener('submit', function () {
      form.querySelectorAll('input').forEach(function (i) { if (i.type === 'number' && !i.value) i.disabled = true; });
    });
  });

  /* ---------- Mark the current top-level menu item ---------- */
  (function () {
    var path = location.pathname.replace(/\/$/, '');
    if (!path) return;
    document.querySelectorAll('.harris-nav > ul > li').forEach(function (li) {
      var a = li.querySelector(':scope > a');
      var href = a && a.getAttribute('href');
      if (!href || href === '/') return;
      href = href.replace(/\/$/, '');
      if (path === href || path.indexOf(href + '/') === 0) li.classList.add('is-current');
    });
  })();


  /* ---------- Quick view ---------- */
  (function () {
    var root = document.querySelector('[data-qv]');
    if (!root) return;
    var body = root.querySelector('[data-qv-body]');
    var lastFocus = null;

    var releaseTrap = null;

    function close() {
      root.hidden = true;
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      if (lastFocus) lastFocus.focus();
    }
    function open(handle, trigger) {
      lastFocus = trigger || null;
      if (releaseTrap) releaseTrap();
      releaseTrap = trapFocus(root);
      var closeBtn = root.querySelector('.harris-qv__close');
      if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 40);
      body.innerHTML = '<p class="harris-qv__loading">Loading…</p>';
      root.hidden = false;
      // Hiding the scrollbar reclaims its width and shifts the page. Pad it back.
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      if (sbw > 0) document.documentElement.style.paddingRight = sbw + 'px';
      var base = location.pathname.indexOf('/products/') === 0 ? '/' : (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(base.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { if (!r.ok) throw new Error('not found'); return r.json(); })
        .then(function (p) { body.innerHTML = render(p, base); })
        .catch(function () { body.innerHTML = '<p class="harris-qv__loading">Sorry, this product could not be loaded.</p>'; });
    }
    function esc(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function render(p, base) {
      var money = moneyFormatter();
      var img = p.featured_image ? '<img src="' + esc(p.featured_image) + '" alt="' + esc(p.title) + '" width="800" height="800">' : '';
      var opts = '';
      if (p.variants && p.variants.length > 1) {
        opts = '<label class="harris-qv__label" for="qv-variant">' + esc((p.options && p.options[0] && (p.options[0].name || p.options[0])) || 'Options') + '</label>' +
          '<select id="qv-variant" class="harris-qv__select" name="id">' +
          p.variants.map(function (v) {
            return '<option value="' + v.id + '" data-price="' + v.price + '"' + (v.available ? '' : ' disabled') + '>' +
              esc(v.title) + (v.available ? '' : ' — Sold out') + '</option>';
          }).join('') + '</select>';
      } else if (p.variants && p.variants.length === 1) {
        opts = '<input type="hidden" name="id" value="' + p.variants[0].id + '">';
      }
      var desc = p.description ? '<div class="harris-qv__desc">' + p.description + '</div>' : '';
      return '<div class="harris-qv__media">' + img + '</div>' +
        '<div class="harris-qv__detail">' +
          '<h2 class="harris-qv__title">' + esc(p.title) + '</h2>' +
          '<p class="harris-qv__price" data-qv-price>' + (p.available ? money(p.price) : 'Sold out') + '</p>' +
          desc +
          '<form method="post" action="/cart/add" class="harris-qv__form">' +
            '<input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="&#x2713;">' +
            opts +
            '<button type="submit" class="harris-btn harris-qv__add"' + (p.available ? '' : ' disabled') + '>' + (p.available ? 'Add to cart' : 'Sold out') + '</button>' +
          '</form>' +
          '<a class="harris-arrow-link harris-qv__full" href="' + base.replace(/\/$/, '') + '/products/' + encodeURIComponent(p.handle) + '">View full details<svg viewBox="0 0 20 12" aria-hidden="true"><path d="M0 6 L17 6 M12 1 L17 6 L12 11"/></svg></a>' +
        '</div>';
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quickview]');
      if (trigger) { e.preventDefault(); open(trigger.getAttribute('data-quickview'), trigger); return; }
      if (e.target.closest('[data-qv-close]')) { close(); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) close(); });
    body.addEventListener('change', function (e) {
      var sel = e.target.closest('#qv-variant');
      if (!sel) return;
      var price = sel.options[sel.selectedIndex].getAttribute('data-price');
      var el = body.querySelector('[data-qv-price]');
      if (price && el) el.textContent = moneyFormatter()(parseInt(price, 10));
    });
  })();

  /* ---------- Collection sort submits on change ---------- */
  document.querySelectorAll('[data-sort-form]').forEach(function (form) {
    form.addEventListener('change', function () { form.submit(); });
  });

})();

/* Section reveal on scroll. Opt out with prefers-reduced-motion; no-JS shows everything. */
(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var STAGGER = [
    '.harris-features__grid', '.harris-grid', '.harris-clist__grid',
    '.harris-tiles__grid', '.harris-footer__grid--blocks', '.harris-sitemap__grid',
    '.harris-value-grid__grid', '.harris-team__grid', '.harris-rel-grid'
  ].join(',');

  var SOLO = [
    '.harris-split__content', '.harris-split__media', '.harris-sec-head',
    '.harris-banner__inner', '.harris-colltext__inner', '.harris-quote',
    '.harris-custom-card__card', '.harris-footer__band-inner'
  ].join(',');

  function mark() {
    document.querySelectorAll(STAGGER).forEach(function (el) {
      if (!el.dataset.harrisReveal) { el.classList.add('harris-reveal-stagger'); el.dataset.harrisReveal = '1'; }
    });
    document.querySelectorAll(SOLO).forEach(function (el) {
      if (!el.dataset.harrisReveal) { el.classList.add('harris-reveal'); el.dataset.harrisReveal = '1'; }
    });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  function observe() {
    mark();
    document.querySelectorAll('.harris-reveal, .harris-reveal-stagger').forEach(function (el) {
      if (el.dataset.harrisObserved) return;
      el.dataset.harrisObserved = '1';
      // already on screen at load: show without animating in
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) { el.classList.add('is-in'); return; }
      io.observe(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
  else observe();
  // theme editor re-renders a section
  document.addEventListener('shopify:section:load', observe);
})();

/* FAQ accordion. The motion is pure CSS (grid-template-rows 0fr -> 1fr), so
   there is no animation bookkeeping to go out of step. JS only flips state. */
(function () {
  function wire(root) {
    (root || document).querySelectorAll('.harris-faq__q-btn').forEach(function (btn) {
      if (btn.dataset.harrisFaq) return;
      btn.dataset.harrisFaq = '1';
      btn.addEventListener('click', function () {
        var item = btn.closest('.harris-faq__item');
        var open = item.classList.contains('is-open');
        var wrap = item.closest('.harris-faq__wrap') || document;
        if (!open) {
          wrap.querySelectorAll('.harris-faq__item.is-open').forEach(function (o) {
            if (o === item) return;
            o.classList.remove('is-open');
            var b = o.querySelector('.harris-faq__q-btn');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
        }
        item.classList.toggle('is-open', !open);
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { wire(); });
  else wire();
  document.addEventListener('shopify:section:load', function (e) { wire(e.target); });
})();
